import { useTranslation } from 'react-i18next';
import { HandCoins, LayoutDashboard, QrCode, Smartphone, type LucideIcon } from 'lucide-react';

import { cn } from '@/utils/cn';

import { LANDING_SURFACE_SCREENSHOTS, type LandingSurfaceKey } from './landing-assets';
import { LandingReveal, LandingSection } from './landing-shared';

const SURFACES: ReadonlyArray<{
  key: LandingSurfaceKey;
  icon: LucideIcon;
  titleKey: `surfaces.${LandingSurfaceKey}.title`;
  bodyKey: `surfaces.${LandingSurfaceKey}.body`;
}> = [
  { key: 'console', icon: LayoutDashboard, titleKey: 'surfaces.console.title', bodyKey: 'surfaces.console.body' },
  { key: 'app', icon: Smartphone, titleKey: 'surfaces.app.title', bodyKey: 'surfaces.app.body' },
  { key: 'kiosk', icon: QrCode, titleKey: 'surfaces.kiosk.title', bodyKey: 'surfaces.kiosk.body' },
  { key: 'give', icon: HandCoins, titleKey: 'surfaces.give.title', bodyKey: 'surfaces.give.body' },
];

function SurfaceScreenshot({
  surfaceKey,
  alt,
}: Readonly<{ surfaceKey: LandingSurfaceKey; alt: string }>) {
  const { width, height } = LANDING_SURFACE_SCREENSHOTS[surfaceKey];
  const portrait = height > width;

  return (
    <div className="landing-surface-shot flex justify-center bg-white p-3 sm:p-4">
      <img
        src={LANDING_SURFACE_SCREENSHOTS[surfaceKey].src}
        alt={alt}
        width={width}
        height={height}
        className={cn(
          'block h-auto max-w-full',
          portrait ? 'max-h-[17rem] w-auto sm:max-h-[19rem]' : 'w-full',
        )}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

export const LandingSurfacesSection = () => {
  const { t } = useTranslation('landing');

  return (
    <LandingSection
      id="surfaces"
      title={t('surfaces.title')}
      subtitle={t('surfaces.subtitle')}
      centered
      onBrand
      className="landing-section-separator py-16 sm:py-20 lg:py-24"
    >
      <ul className="mt-12 grid gap-6 sm:grid-cols-2 sm:gap-8 lg:gap-10">
        {SURFACES.map(({ key, icon: Icon, titleKey, bodyKey }, index) => {
          const screenshot = LANDING_SURFACE_SCREENSHOTS[key];
          const audienceKey = screenshot.audienceKey;

          return (
            <LandingReveal key={key} as="li" delayMs={index * 60}>
              <article className="landing-surface-card flex h-full flex-col overflow-hidden rounded-2xl border border-white/20 bg-white/8">
                <SurfaceScreenshot surfaceKey={key} alt={t(screenshot.altKey)} />
                <div className="flex flex-1 flex-col p-5 sm:p-6">
                  <span className="landing-feature-icon landing-feature-icon-on-brand w-fit" aria-hidden>
                    <Icon className="size-5 stroke-[1.75]" />
                  </span>
                  <p className="mt-4 text-caption font-semibold uppercase tracking-[0.12em] text-on-brand/70">
                    {t(`moments.${audienceKey}.title`)}
                  </p>
                  <h3 className="mt-1 text-card-title font-semibold tracking-tight">{t(titleKey)}</h3>
                  <p className="mt-2 flex-1 text-body-sm leading-relaxed text-on-brand/90">{t(bodyKey)}</p>
                </div>
              </article>
            </LandingReveal>
          );
        })}
      </ul>
    </LandingSection>
  );
};
